import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {DocumentSourceTypeEnum} from 'nutshell-graphql-types';
import type {
    RemoveContextDocument as RemoveContextDocumentMutation,
    RemoveContextDocumentVariables as RemoveContextDocumentMutationVariables,
} from 'nutshell-graphql-types';

import REMOVE_CONTEXT_DOCUMENT from '../mutations/remove-context-document.graphql';
import GET_CONTEXT_DOCUMENTS from '../queries/get-context-documents.graphql';

export const useRemoveContextDocument = (): {
    removeContextDocument: (input: {
        documentId: string;
        sourceType: DocumentSourceTypeEnum;
    }) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [removeContextDocumentMutation, {loading, error}] = useMutation<
        RemoveContextDocumentMutation,
        RemoveContextDocumentMutationVariables
    >(REMOVE_CONTEXT_DOCUMENT);

    return {
        removeContextDocument: (input: {
            documentId: string;
            sourceType: DocumentSourceTypeEnum;
        }) => {
            return removeContextDocumentMutation({
                variables: {
                    input: {
                        documentId: input.documentId,
                    },
                },
                refetchQueries: ['GetContextDocumentUsage'],
                optimisticResponse: {
                    // @ts-expect-error
                    __typename: 'Mutation',
                    contextDocumentRemove: {
                        __typename: 'ContextDocument',
                        id: input.documentId,
                    },
                },
                update: (proxy, {data}) => {
                    const cacheResult = proxy.readQuery({
                        query: GET_CONTEXT_DOCUMENTS,
                        variables: {
                            includeExcluded: true,
                            sourceType: input.sourceType,
                        },
                    });

                    if (
                        !cacheResult ||
                        // @ts-expect-error
                        !cacheResult.contextDocuments ||
                        !data ||
                        !data.contextDocumentRemove
                    ) {
                        return;
                    }

                    proxy.writeQuery({
                        query: GET_CONTEXT_DOCUMENTS,
                        variables: {
                            includeExcluded: true,
                            sourceType: input.sourceType,
                        },
                        data: {
                            // @ts-expect-error
                            contextDocuments: cacheResult.contextDocuments.filter(
                                // @ts-expect-error
                                (document) => document.id !== input.documentId
                            ),
                        },
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
